import { defineStore } from 'pinia'
import { onUnmounted } from 'vue'

export interface CommandMap {
  'entity:new': { type: string }
  'entity:view': { type: string; id: string }
  'entity:edit': { type: string; id: string }
  'entity:delete': { type: string; ids: string[] }
}

export interface EventMap {
  'entity:created': { type: string; ids: string[] }
  'entity:updated': { type: string; ids: string[] }
  'entity:deleted': { type: string; ids: string[] }
}

export type Command = keyof CommandMap
export type Event = keyof EventMap

export type CommandEventMap = CommandMap & EventMap

export type EventListener<K extends keyof CommandEventMap> = (
  payload: CommandEventMap[K]
) => void | Promise<void>

export type Unsubscribe = () => void

export const useEvents = defineStore('actionEvents', () => {
  const listeners: { [K in keyof CommandEventMap]?: EventListener<K>[] } = {}

  const on = <K extends keyof CommandEventMap>(
    eventName: K,
    listener: EventListener<K>
  ): Unsubscribe => {
    const genericListener = listener as EventListener<keyof CommandEventMap>

    if (!listeners[eventName]) {
      listeners[eventName] = []
    }
    listeners[eventName]!.push(genericListener)

    const unsubscribe = () => {
      const actionListeners = listeners[eventName]
      if (!actionListeners) {
        return
      }

      const index = actionListeners.indexOf(genericListener)

      if (index > -1) {
        actionListeners.splice(index, 1)
      }
    }

    return unsubscribe
  }

  const emit = async <K extends keyof CommandEventMap>(
    eventName: K,
    ...payload: CommandEventMap[K] extends void ? [] : [CommandEventMap[K]]
  ): Promise<void> => {
    const actionListeners = (listeners[eventName] as EventListener<K>[]) ?? []

    const promises = actionListeners.map((listener) => listener(payload[0] as CommandEventMap[K]))

    const results = await Promise.allSettled(promises)

    for (const result of results) {
      if (result.status === 'rejected') {
        console.error(`[Event] A listener for "${eventName}" failed:`, result.reason)
      }
    }
  }

  return {
    emit,
    on
  }
})

export const useEventListener = <K extends keyof CommandEventMap>(
  eventName: K,
  listener: EventListener<K>
) => {
  const actionEventStore = useEvents()
  const unsubscribe = actionEventStore.on(eventName, listener)
  onUnmounted(unsubscribe)
}
